import {
    Card,
    Colors,
    Heading,
    Icon,
    MultiSelectOutlined,
    ProgressBar,
    Text,
    type IconName,
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@amelio/ui-library';

import type { TranslationKey } from '@/i18n-key';
import type { ProgressTrackingSummary } from '@/types/cycle-evaluation/ProgressTrackingSummary';

import { useGetProgressTrackingSummary } from '@/api/cycle-evaluation-services/progress-tracking';
import { Bone, Skeleton } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';
import {
    cycleBlockBodyText,
    cycleBlockCard,
    cycleBlockSurfaceBackground,
    cycleBlockTitleText,
    cycleBreakdownDotSize,
    cycleCardRadius,
    cycleFiltersRow,
    cycleGapLg,
    cycleGapSm,
    cycleGapXs,
    cycleMarginBottom2Xl,
    cycleMarginBottomLg,
    cycleMarginTop2Xl,
    cycleProgressBarThickness,
    cycleSectionIconFrame,
    cycleSectionIconSize,
    cycleSectionSubtitleText,
    cycleSectionTitleText,
} from '@/utils/cycleStyles';

const COMING_SOON_TOOLTIP_KEY: TranslationKey = 'cycleEvaluationDashboard.comingSoonTooltip';

interface ProgressTrackingComingSoonFilterProps {
    labelKey: TranslationKey;
    iconName: IconName;
    dataTestId: string;
}

const ProgressTrackingComingSoonFilter = ({
    labelKey,
    iconName,
    dataTestId,
}: ProgressTrackingComingSoonFilterProps) => {
    const { t } = useAmelioTranslations();
    const label = t(labelKey);

    return (
        <TooltipProvider delayDuration={200}>
            <Tooltip>
                <TooltipTrigger asChild>
                    <div
                        role={'button'}
                        tabIndex={0}
                        aria-disabled={true}
                        aria-label={label}
                        data-testid={dataTestId}
                    >
                        <div aria-hidden={true}>
                            <MultiSelectOutlined
                                options={[]}
                                disabled
                                placeholder={label}
                                icon={
                                    <Icon
                                        name={iconName}
                                        color={Colors.BASE_400}
                                    />
                                }
                                dataTestId={`${dataTestId}-control`}
                            />
                        </div>
                    </div>
                </TooltipTrigger>

                <TooltipContent side={'top'}>
                    <p>{t(COMING_SOON_TOOLTIP_KEY)}</p>
                </TooltipContent>
            </Tooltip>
        </TooltipProvider>
    );
};

const ProgressTrackingFilters = () => (
    <div
        className={cycleFiltersRow}
        data-testid={'progress-tracking-filters'}
    >
        <ProgressTrackingComingSoonFilter
            labelKey={'cycleEvaluationDashboard.progressTracking.filters.cycle'}
            iconName={'time-calendar'}
            dataTestId={'progress-tracking-cycle-filter'}
        />

        <ProgressTrackingComingSoonFilter
            labelKey={'cycleEvaluationDashboard.progressTracking.filters.evaluationType'}
            iconName={'settings-category'}
            dataTestId={'progress-tracking-evaluation-type-filter'}
        />
    </div>
);

type BreakdownKey = 'completed' | 'inProgress' | 'notStarted';

const BREAKDOWN_ORDER: BreakdownKey[] = ['completed', 'inProgress', 'notStarted'];

const BREAKDOWN_DOT_COLOR: Record<BreakdownKey, string> = {
    completed: 'bg-success',
    inProgress: 'bg-chart-orange',
    notStarted: 'bg-muted',
};

const BREAKDOWN_LABEL_KEY: Record<BreakdownKey, TranslationKey> = {
    completed: 'cycleEvaluationDashboard.progressTracking.breakdown.completed',
    inProgress: 'cycleEvaluationDashboard.progressTracking.breakdown.inProgress',
    notStarted: 'cycleEvaluationDashboard.progressTracking.breakdown.notStarted',
};

// The API service casts the response body without validating it; a field can be absent at runtime.
const isCompleteSummary = (summary: ProgressTrackingSummary): boolean =>
    [
        summary.totalCount,
        summary.completedCount,
        summary.inProgressCount,
        summary.notStartedCount,
        summary.completionRate,
    ].every((value) => typeof value === 'number' && Number.isFinite(value));

const ProgressTrackingPanelHeader = () => {
    const { t } = useAmelioTranslations();

    return (
        <div className={'flex min-w-0 items-start gap-[12px]'}>
            <div
                className={`bg-base-50 flex shrink-0 items-center justify-center shadow-sm ${cycleSectionIconFrame} rounded-lg`}
                aria-hidden={true}
            >
                <Icon
                    name={'edit-clipboard-tick'}
                    className={cycleSectionIconSize}
                    color={Colors.STEP_RAIL_VIOLET}
                />
            </div>

            <div className={'flex min-w-0 flex-col gap-1.5'}>
                <Heading
                    level={3}
                    weight={'medium'}
                    className={cycleSectionTitleText}
                >
                    {t('cycleEvaluationDashboard.progressTracking.title')}
                </Heading>

                <Text
                    size={'small'}
                    weight={'medium'}
                    color={Colors.BASE_500}
                    className={`wrap-break-word ${cycleSectionSubtitleText}`}
                >
                    {t('cycleEvaluationDashboard.progressTracking.description')}
                </Text>
            </div>
        </div>
    );
};

const ProgressTrackingEmptyState = () => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={'py-6 text-center'}
            data-testid={'progress-tracking-empty-state'}
        >
            <Text>{t('cycleEvaluationDashboard.progressTracking.emptyState')}</Text>
        </div>
    );
};

interface BreakdownTileProps {
    breakdownKey: BreakdownKey;
    count: number;
}

const BreakdownTile = ({ breakdownKey, count }: BreakdownTileProps) => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={`flex items-center ${cycleGapSm}`}
            data-testid={`progress-tracking-breakdown-${breakdownKey}`}
        >
            <span
                className={`${cycleBreakdownDotSize} shrink-0 rounded-full ${BREAKDOWN_DOT_COLOR[breakdownKey]}`}
                aria-hidden={true}
            />

            <Text
                size={'small'}
                color={Colors.BASE_500}
                className={`flex items-baseline ${cycleGapXs} ${cycleBlockBodyText}`}
            >
                {t('cycleEvaluationDashboard.progressTracking.breakdownLabel', {
                    label: t(BREAKDOWN_LABEL_KEY[breakdownKey]),
                })}

                <Text
                    as={'span'}
                    size={'small'}
                    weight={'bold'}
                    color={Colors.NAVY}
                    className={cycleBlockBodyText}
                >
                    {count}
                </Text>
            </Text>
        </div>
    );
};

const ProgressTrackingTeamSection = () => {
    const { t } = useAmelioTranslations();

    return (
        <div
            className={`${cycleBlockSurfaceBackground} ${cycleBlockCard}`}
            data-testid={'progress-tracking-team-block'}
        >
            <Heading
                level={4}
                weight={'semibold'}
                className={`${cycleMarginBottomLg} ${cycleBlockTitleText}`}
            >
                {t('cycleEvaluationDashboard.progressTracking.team.title')}
            </Heading>

            <Text
                size={'small'}
                color={Colors.BASE_500}
                className={cycleBlockBodyText}
            >
                {t('cycleEvaluationDashboard.progressTracking.team.comingSoon')}
            </Text>
        </div>
    );
};

const ProgressTrackingSummaryContent = ({
    totalCount,
    completedCount,
    inProgressCount,
    notStartedCount,
    completionRate,
}: ProgressTrackingSummary) => {
    const { t } = useAmelioTranslations();

    if (totalCount === 0) {
        return <ProgressTrackingEmptyState />;
    }

    const countsByKey: Record<BreakdownKey, number> = {
        completed: completedCount,
        inProgress: inProgressCount,
        notStarted: notStartedCount,
    };

    return (
        <div
            className={`${cycleBlockSurfaceBackground} ${cycleBlockCard} ${cycleMarginBottom2Xl}`}
            data-testid={'progress-tracking-overview'}
        >
            <Heading
                level={4}
                weight={'semibold'}
                className={`${cycleMarginBottomLg} ${cycleBlockTitleText}`}
            >
                {t('cycleEvaluationDashboard.progressTracking.metric')}
            </Heading>

            <div
                className={`flex items-center ${cycleGapLg} ${cycleMarginBottomLg}`}
                data-testid={'progress-tracking-rate-row'}
            >
                <ProgressBar
                    value={completionRate}
                    gradient={'VIOLET_TO_PURPLE'}
                    size={'lg'}
                    className={`flex-1 ${cycleProgressBarThickness}`}
                    ariaLabel={t('cycleEvaluationDashboard.progressTracking.metric')}
                    dataTestId={'progress-tracking-rate'}
                />

                <Text
                    size={'small'}
                    weight={'medium'}
                    color={Colors.NAVY}
                    className={`shrink-0 ${cycleBlockBodyText}`}
                >
                    {`${completionRate}%`}
                </Text>
            </div>

            <Text
                size={'small'}
                color={Colors.BASE_500}
                className={cycleBlockBodyText}
            >
                {t('cycleEvaluationDashboard.progressTracking.submittedOfTotal', {
                    submitted: completedCount,
                    total: totalCount,
                })}
            </Text>

            <div className={`grid grid-cols-1 sm:grid-cols-3 ${cycleGapLg} ${cycleMarginTop2Xl}`}>
                {BREAKDOWN_ORDER.map((breakdownKey) => (
                    <BreakdownTile
                        key={breakdownKey}
                        breakdownKey={breakdownKey}
                        count={countsByKey[breakdownKey]}
                    />
                ))}
            </div>
        </div>
    );
};

export const ProgressTracking = () => {
    const { data, isLoading } = useGetProgressTrackingSummary();

    return (
        <Card
            padding={'none'}
            className={`relative flex flex-col justify-center max-md:gap-[16px] max-md:p-[16px] md:gap-[24px] md:p-[48px] ${cycleCardRadius}`}
            borderColor={Colors.WHITE}
            dataTestId={'progress-tracking-panel'}
        >
            <div
                className={`flex w-full justify-between gap-[24px] max-xl:flex-col max-xl:items-start xl:flex-row xl:items-start ${cycleMarginBottom2Xl}`}
                data-testid={'progress-tracking-header-row'}
            >
                <ProgressTrackingPanelHeader />

                <ProgressTrackingFilters />
            </div>

            {isLoading || !data || !isCompleteSummary(data) ? (
                <Skeleton className={'flex flex-col gap-4'}>
                    <Bone className={'h-8 w-64'} />

                    <Bone className={'h-24 w-full'} />
                </Skeleton>
            ) : (
                <ProgressTrackingSummaryContent
                    totalCount={data.totalCount}
                    completedCount={data.completedCount}
                    inProgressCount={data.inProgressCount}
                    notStartedCount={data.notStartedCount}
                    completionRate={data.completionRate}
                />
            )}

            <ProgressTrackingTeamSection />
        </Card>
    );
};
